<template>
  <LoaderComponent v-if="servicesList === 'await'" />

  <div v-else :class="style.serviceComponent">
    <ActionModal
      v-model="isDeleteDirectionModalOpen"
      title="Удалить направление"
      :actions="buttonsDeleteModal"
    />

    <SectionHeaderBlock
      title="Цены"
      subTitle="Добавьте направление, чтобы указать цены."
      :conditionSubTitle="!servicesListEntries.length"
    >
      <template #buttons>
        <PrimaryButton
          text="Добавить"
          @click="addDirection('Новое направление')"
        />
      </template>
    </SectionHeaderBlock>

    <div :class="style.content">
      <AccordionBase
        v-for="([category, data], index) in servicesListEntries"
        :key="category"
        :class="style.accordion"
        :classHeader="[style.accordionHeader]"
        :text="category"
        :defaultOpen="false"
        :showChevronWithActions="true"
        :isHideActions="false"
      >
        <template #title>
          <SectionHeaderEditable
            v-model="editingCategoryValue"
            :edit="editingCategoryName === category"
            @save="applyCategoryRename"
            @cancel="cancelCategoryRename"
          >
            {{ category }}
          </SectionHeaderEditable>
        </template>

        <template #actions>
          <div :class="style.buttonsAccordion">
            <ActionMenu
              :modelValue="
                selectedCategoryName === category && isShowPanelSetting
              "
              :actions="getDirectionPopupActions(category, index)"
              placement="bottom-end"
              :offset="8"
              :width="226"
              @update:modelValue="setDirectionMenuValue(category, $event)"
            >
              <template #trigger="{isOpen, toggle}">
                <TabIcon
                  size="28"
                  variant="ghost"
                  :active="isOpen"
                  :icon="moreIcon"
                  @click.stop="toggle"
                />
              </template>
            </ActionMenu>
            <ButtonIcon
              :icon="addIcon"
              @click="addServices(category)"
              size="28"
              variant="ghost"
            />
          </div>
        </template>

        <DragDropListBase
          :modelValue="data"
          item-key-field="__dragKey"
          name-field="name"
          value-field="cost"
          name-placeholder="Введите название"
          value-placeholder="1000"
          :suffix="currencySymbol"
          value-input-mode="text"
          @update:modelValue="updateCategoryItems(category, $event)"
        >
          <template #actions="{remove}">
            <ButtonIcon
              :icon="closeIcon"
              size="28"
              @click="removeService(category, remove)"
            />
          </template>
        </DragDropListBase>
      </AccordionBase>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent, markRaw} from "vue";
import style from "./StyleServices.module.scss";

import PrimaryButton from "@/elements/UI/button/ButtonStyle/Default/primary/PrimaryButton.vue";
import AccordionBase from "@/elements/UI/accordion/AccordionBase.vue";
import DragDropListBase from "@/elements/UI/list/drag/List/DragDropListBase.vue";
import ActionMenu from "@/elements/UI/popup/ActionMenu/ActionMenu.vue";

import addIcon from "$images/icons/button/Name=Add, Size=16, Tone=Default.svg";
import closeIcon from "$images/icons/button/Name=Close, Size=16, Tone=Default.svg";
import moreIcon from "$images/icons/button/Name=More, Size=16, Tone=Default.svg";
import chevronUp from "$images/icons/button/Name=Chevron-up, Size=16, Tone=Default.svg";
import chevronDown from "$images/icons/button/Name=Chevron-down, Size=16, Tone=Default.svg";
import deleteIconError from "$images/icons/button/Name=Delete, Size=16, Tone=Error.svg";
import renameIcon from "$images/icons/button/Name=Rename, Size=16, Tone=Default.svg";
import SectionHeaderEditable from "@/elements/UI/input/SectionHeaderEditable/SectionHeaderEditable.vue";
import ActionModal from "@/layout/nav/components/modal/modals/ActionModal/ActionModal.vue";
import SectionHeaderBlock from "@/elements/UI/section/SectionHeaderBlock/SectionHeaderBlock.vue";
import ButtonIcon from "@/elements/UI/button/ButtonIcon/ButtonIcon.vue";
import TabIcon from "@/elements/UI/tabs/TabIcon/TabIcon.vue";
import LoaderComponent from "@/elements/UI/overlay/Loader/LoaderComponent.vue";
import GhostButtonError from "@/elements/UI/button/ButtonStyle/Error/ghost/GhostButtonError.vue";
import {API} from "@/api/generated";

type ServiceItem = {
  name: string;
  cost: string;
  checked: boolean;
  __dragKey?: string;
};

type ServicesList = Record<string, ServiceItem[]>;
type RawServicesList = Record<string, Partial<ServiceItem>[]>;

export default defineComponent({
  name: "ServicesComponent",

  components: {
    LoaderComponent,
    TabIcon,
    ButtonIcon,
    SectionHeaderBlock,
    ActionModal,
    SectionHeaderEditable,
    ActionMenu,
    DragDropListBase,
    AccordionBase,
    PrimaryButton
  },

  data() {
    return {
      style,

      servicesList: "await" as ServicesList | "await",
      lastSavedServicesListStr: "",
      isSaving: false,
      saveDebounceTimer: null as number | null,
      isShowPanelSetting: false,
      isDeleteDirectionModalOpen: false,
      selectedCategoryName: null as string | null,
      deletingCategoryName: null as string | null,
      editingCategoryName: null as string | null,
      editingCategoryValue: "",

      addIcon,
      closeIcon,
      moreIcon
    };
  },

  async mounted() {
    window.addEventListener("keydown", this.handleSaveShortcut);

    try {
      await this.$clinicStore.fetchSetting();
      this.applySettingServicesList(this.getSetting?.servicesList);
    } catch (error) {
      console.error("Ошибка при загрузке услуг:", error);
      this.servicesList = {};
      this.lastSavedServicesListStr = JSON.stringify({});
    }
  },

  beforeUnmount() {
    window.removeEventListener("keydown", this.handleSaveShortcut);
    this.clearSaveDebounceTimer();
  },

  methods: {
    handleSaveShortcut(event: KeyboardEvent): void {
      if (!((event.metaKey || event.ctrlKey) && event.code === "KeyS")) {
        return;
      }

      event.preventDefault();
      this.clearSaveDebounceTimer();
      void this.saveSetting();
    },

    clearSaveDebounceTimer(): void {
      if (this.saveDebounceTimer === null) {
        return;
      }

      window.clearTimeout(this.saveDebounceTimer);
      this.saveDebounceTimer = null;
    },

    applySettingServicesList(servicesList: RawServicesList | undefined): void {
      const preparedList = this.prepareServicesList(servicesList || {});

      this.lastSavedServicesListStr = JSON.stringify(
        this.clearDragKeys(preparedList)
      );

      this.servicesList = preparedList;
    },

    startCategoryRename(category: string): void {
      this.editingCategoryName = category;
      this.editingCategoryValue = category;
    },

    cancelCategoryRename(): void {
      this.editingCategoryName = null;
      this.editingCategoryValue = "";
    },

    applyCategoryRename(): void {
      const oldName = this.editingCategoryName;

      if (!oldName) {
        this.cancelCategoryRename();
        return;
      }

      const newName = this.editingCategoryValue.trim();

      if (!newName || newName === oldName) {
        this.cancelCategoryRename();
        return;
      }

      if (this.servicesList === "await") {
        this.cancelCategoryRename();
        return;
      }

      let finalName = newName;
      let counter = 2;

      while (this.servicesList[finalName] && finalName !== oldName) {
        finalName = `${newName} ${counter}`;
        counter++;
      }

      const nextList: ServicesList = {};

      Object.entries(this.servicesList).forEach(([category, items]) => {
        if (category === oldName) {
          nextList[finalName] = items;
        } else {
          nextList[category] = items;
        }
      });

      this.servicesList = nextList;

      if (this.selectedCategoryName === oldName) {
        this.selectedCategoryName = finalName;
      }

      this.cancelCategoryRename();
    },

    setServicesListFromEntries(entries: [string, ServiceItem[]][]): void {
      const nextList: ServicesList = {};

      entries.forEach(([key, value]) => {
        nextList[key] = value;
      });

      this.servicesList = nextList;
    },

    setDirectionMenuValue(category: string, value: boolean): void {
      if (value) {
        this.selectedCategoryName = category;
        this.isShowPanelSetting = true;
        return;
      }

      if (this.selectedCategoryName === category) {
        this.selectedCategoryName = null;
        this.isShowPanelSetting = false;
      }
    },

    getDirectionPopupActions(
      category: string,
      index: number
    ): ListActionItem[] {
      return [
        {
          id: `${category}-move-up`,
          text: "Переместить вверх",
          icon: chevronUp,
          disabled: index <= 0,
          variant: "button",
          handler: () => {
            this.selectedCategoryName = category;
            this.moveSelectedCategoryUp();
          }
        },
        {
          id: `${category}-move-down`,
          text: "Переместить вниз",
          icon: chevronDown,
          disabled: index >= this.servicesListEntries.length - 1,
          variant: "button",
          handler: () => {
            this.selectedCategoryName = category;
            this.moveSelectedCategoryDown();
          }
        },
        {
          id: `${category}-rename`,
          text: "Переименовать",
          icon: renameIcon,
          variant: "button",
          handler: () => {
            this.selectedCategoryName = null;
            this.isShowPanelSetting = false;
            this.startCategoryRename(category);
          }
        },
        {
          id: `${category}-delete`,
          text: "Удалить",
          icon: deleteIconError,
          variant: "button",
          buttonComponent: markRaw(GhostButtonError),
          handler: () => {
            this.openDeleteDirectionModal(category);
          }
        }
      ];
    },

    closeDeleteDirectionModal(): void {
      this.isDeleteDirectionModalOpen = false;
      this.deletingCategoryName = null;
    },

    openDeleteDirectionModal(category: string): void {
      this.deletingCategoryName = category;
      this.selectedCategoryName = null;
      this.isShowPanelSetting = false;
      this.isDeleteDirectionModalOpen = true;
    },

    moveSelectedCategoryUp(): void {
      if (!this.selectedCategoryName) {
        return;
      }

      const currentIndex = this.servicesListEntries.findIndex(
        ([category]) => category === this.selectedCategoryName
      );

      if (currentIndex <= 0) {
        return;
      }

      const updatedEntries = [...this.servicesListEntries];
      const temp = updatedEntries[currentIndex - 1];

      updatedEntries[currentIndex - 1] = updatedEntries[currentIndex];
      updatedEntries[currentIndex] = temp;

      this.setServicesListFromEntries(updatedEntries);
      this.isShowPanelSetting = false;
      this.selectedCategoryName = null;
    },

    moveSelectedCategoryDown(): void {
      if (!this.selectedCategoryName) {
        return;
      }

      const currentIndex = this.servicesListEntries.findIndex(
        ([category]) => category === this.selectedCategoryName
      );

      if (
        currentIndex === -1 ||
        currentIndex >= this.servicesListEntries.length - 1
      ) {
        return;
      }

      const updatedEntries = [...this.servicesListEntries];
      const temp = updatedEntries[currentIndex + 1];

      updatedEntries[currentIndex + 1] = updatedEntries[currentIndex];
      updatedEntries[currentIndex] = temp;

      this.setServicesListFromEntries(updatedEntries);
      this.isShowPanelSetting = false;
      this.selectedCategoryName = null;
    },

    deleteSelectedCategory(): void {
      const categoryForDelete = this.deletingCategoryName;

      if (!categoryForDelete) {
        return;
      }

      if (this.servicesList === "await") {
        return;
      }

      const nextList: ServicesList = {};

      this.servicesListEntries.forEach(([category, items]) => {
        if (category !== categoryForDelete) {
          nextList[category] = items;
        }
      });

      this.servicesList = nextList;
      this.isDeleteDirectionModalOpen = false;
      this.isShowPanelSetting = false;
      this.selectedCategoryName = null;
      this.deletingCategoryName = null;
    },

    getDragKey(category: string, index: number): string {
      return `${category}_${index}_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2, 8)}`;
    },

    normalizeTrimmedValue(value: string): string {
      return String(value ?? "").trim();
    },

    formatCost(value: string): string {
      const currentValue = String(value ?? "");
      const valueSplit = currentValue.split(" ");

      let prefix = "";
      let mainPart = "";

      valueSplit.forEach((element, key) => {
        if (key === 0 && currentValue.toUpperCase().startsWith("ОТ")) {
          prefix = `${element} `;
        } else {
          mainPart += element;
        }
      });

      mainPart = mainPart.replace(/[^\d]/g, "");

      if (mainPart.length > 4) {
        mainPart = mainPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
      }

      return prefix + mainPart;
    },

    updateCategoryItems(direction: string, updatedItems: ServiceItem[]): void {
      const servicesList = this.servicesList;

      if (servicesList === "await") {
        return;
      }

      servicesList[direction] = updatedItems.map((item, index) => ({
        name: this.normalizeTrimmedValue(item.name),
        cost: this.formatCost(item.cost),
        checked: Boolean(item.checked),
        __dragKey: item.__dragKey || this.getDragKey(direction, index)
      }));
    },

    removeService(direction: string, remove: () => void): void {
      const servicesList = this.servicesList;

      if (servicesList === "await") {
        return;
      }

      remove();

      if (servicesList[direction]) {
        servicesList[direction] = [...servicesList[direction]];
      }
    },

    async saveSetting(): Promise<boolean> {
      if (this.isSaving) {
        return false;
      }

      try {
        const servicesList = this.servicesList;

        if (servicesList === "await") {
          return false;
        }

        const servicesListForSave = this.clearDragKeys(servicesList);
        const currentServicesListStr = JSON.stringify(servicesListForSave);

        if (this.lastSavedServicesListStr === currentServicesListStr) {
          return false;
        }

        this.isSaving = true;

        const dataClinic = {
          servicesList: servicesListForSave
        };

        const data = await API.updateSetting({
          body: {
            renaming: {},
            setting: dataClinic
          }
        });

        const updatedServicesList = data?.servicesList;

        if (!updatedServicesList) {
          this.lastSavedServicesListStr = currentServicesListStr;
          return true;
        }

        const preparedList = this.prepareServicesList(
          updatedServicesList as RawServicesList
        );
        const actualServicesListStr = JSON.stringify(
          this.clearDragKeys(this.servicesList)
        );

        this.lastSavedServicesListStr = currentServicesListStr;

        if (actualServicesListStr === currentServicesListStr) {
          this.servicesList = preparedList;
          this.lastSavedServicesListStr = JSON.stringify(
            this.clearDragKeys(preparedList)
          );
        }

        return true;
      } catch (error) {
        console.error("Ошибка при сохранении servicesList:", error);

        return false;
      } finally {
        this.isSaving = false;

        if (this.servicesList !== "await" && this.hasUnsavedChanges) {
          this.updateStatusSave();
        }
      }
    },

    addServices(direction: string): void {
      const servicesList = this.servicesList;

      if (servicesList === "await") {
        return;
      }

      if (!servicesList[direction]) {
        servicesList[direction] = [];
      }

      const nextIndex = servicesList[direction].length;

      servicesList[direction].push({
        name: "",
        cost: "",
        checked: false,
        __dragKey: this.getDragKey(direction, nextIndex)
      });
    },

    addDirection(direction: string | null = null): void {
      if (this.servicesList === "await") {
        this.servicesList = {};
      }

      const servicesList = this.servicesList as ServicesList | "await";

      if (servicesList === "await") {
        return;
      }

      const baseName =
        direction && direction.trim() !== ""
          ? direction.trim()
          : "Новое направление";

      let finalName = baseName;
      let counter = 2;

      while (servicesList[finalName]) {
        finalName = `${baseName} ${counter}`;
        counter++;
      }

      servicesList[finalName] = [];
    },

    clearDragKeys<T extends ServicesList | "await">(
      list: T
    ): T extends "await" ? "await" : ServicesList {
      if (list === "await") {
        return list as unknown as T extends "await" ? "await" : ServicesList;
      }

      const cleaned: ServicesList = {};

      Object.entries(list).forEach(([key, items]) => {
        cleaned[key] = items
          .map((item) => ({
            name: this.normalizeTrimmedValue(item.name),
            cost: this.formatCost(item.cost),
            checked: Boolean(item.checked)
          }))
          .filter((item) => item.name || item.cost);
      });

      return cleaned as unknown as T extends "await" ? "await" : ServicesList;
    },

    prepareServicesList(servicesList: RawServicesList): ServicesList {
      const newList: ServicesList = {};

      Object.entries(servicesList).forEach(([key, servicesArray]) => {
        newList[key] = [];

        servicesArray.forEach((service, index) => {
          let cost = String(service.cost ?? "");
          const costSplit = cost.split(" ");
          let prefix: string | undefined;

          if (costSplit[0] && costSplit[0].toLowerCase() === "от") {
            prefix = costSplit[0];
          }

          cost = cost.replace(/\s/g, "");

          const matched = cost.match(/\d+/g);

          cost = matched ? matched.join("") : "";

          if (cost.length > 4) {
            cost = cost.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
          }

          if (prefix !== undefined && cost !== "") {
            cost = `${prefix} ${cost}`;
          }

          newList[key].push({
            name: service.name ?? "",
            cost,
            checked: Boolean(service.checked),
            __dragKey: this.getDragKey(key, index)
          });
        });
      });

      return newList;
    },

    updateStatusSave(): void {
      if (this.isSaving || this.servicesList === "await") {
        return;
      }

      const currentList = this.clearDragKeys(this.servicesList);
      const currentString = JSON.stringify(currentList);

      if (this.lastSavedServicesListStr !== currentString) {
        this.clearSaveDebounceTimer();

        this.saveDebounceTimer = window.setTimeout(() => {
          this.saveDebounceTimer = null;

          const actualString = JSON.stringify(
            this.clearDragKeys(this.servicesList)
          );

          if (currentString === actualString) {
            void this.saveSetting();
          }
        }, 1000);
      }
    }
  },

  computed: {
    getClinic() {
      return this.$clinicStore.getClinic;
    },

    getSetting() {
      return this.$clinicStore.getSetting;
    },

    currencySymbol(): string {
      return this.getClinic?.currency?.symbol || "₽";
    },

    servicesListEntries(): [string, ServiceItem[]][] {
      if (this.servicesList === "await") {
        return [];
      }

      return Object.entries(this.servicesList);
    },

    getServicesListStr(): string {
      return JSON.stringify(this.clearDragKeys(this.servicesList));
    },

    hasUnsavedChanges(): boolean {
      return this.getServicesListStr !== this.lastSavedServicesListStr;
    },

    buttonsDeleteModal(): ListActionItem[] {
      return [
        {
          text: "Удалить",
          handler: this.deleteSelectedCategory
        },
        {
          text: "Отмена",
          handler: this.closeDeleteDirectionModal
        }
      ];
    }
  },

  watch: {
    getSetting(data): void {
      if (!data?.servicesList) {
        return;
      }

      if (
        this.servicesList !== "await" &&
        (this.hasUnsavedChanges || this.isSaving)
      ) {
        return;
      }

      this.applySettingServicesList(data.servicesList as ServicesList);
    },

    getServicesListStr(): void {
      this.updateStatusSave();
    }
  }
});
</script>
